import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentCompletePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking"); // "checking", "success", "failed", "expired"
  const [orderData, setOrderData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const sumupCheckoutId = searchParams.get("sumup_checkout_id");
    
    if (sumupCheckoutId) {
      // Pagamento SumUp
      checkSumUpPaymentStatus(sumupCheckoutId);
    } else if (sessionId) {
      // Pagamento Stripe
      checkPaymentStatus(sessionId);
    } else {
      setStatus("failed");
    }
  }, [searchParams]);

  const checkSumUpPaymentStatus = async (checkoutReference) => {
    try {
      const response = await axios.get(`${API}/payments/sumup/status/${checkoutReference}`);
      
      if (response.data.status === "paid") {
        setStatus("success");
        setOrderData({
          orderId: response.data.order_id,
          orderNumber: response.data.order_number
        });
        clearCart();
      } else if (response.data.status === "expired" || response.data.status === "failed") {
        setStatus("failed");
      } else if (response.data.status === "pending") {
        // Riprova dopo 2 secondi
        if (attempts < maxAttempts) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
            checkSumUpPaymentStatus(checkoutReference);
          }, 2000);
        } else {
          setStatus("failed");
        }
      } else {
        setStatus("failed");
      }
    } catch (error) {
      console.error("Errore verifica pagamento SumUp:", error);
      if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkSumUpPaymentStatus(checkoutReference);
        }, 2000);
      } else {
        setStatus("failed");
      }
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.status === "paid") {
        setStatus("success");
        setOrderData({
          orderId: response.data.order_id,
          orderNumber: response.data.order_number
        });
        clearCart();
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else if (response.data.status === "pending") {
        // Riprova dopo 2 secondi
        if (attempts < maxAttempts) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
            checkPaymentStatus(sessionId);
          }, 2000);
        } else {
          setStatus("failed");
        }
      } else {
        setStatus("failed");
      }
    } catch (error) {
      console.error("Errore verifica pagamento:", error);
      if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkPaymentStatus(sessionId);
        }, 2000);
      } else {
        setStatus("failed");
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full text-center">
          
          {status === "checking" && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
              <Loader2 className="w-16 h-16 text-green-500 mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-4">
                Verifica pagamento in corso...
              </h1>
              <p className="text-zinc-400 mb-4">
                Stiamo verificando il tuo pagamento con SumUp. Non chiudere questa pagina.
              </p>
              {attempts > 0 && (
                <p className="text-zinc-500 text-sm">
                  Tentativo {attempts}/{maxAttempts}...
                </p>
              )}
            </div>
          )}

          {status === "success" && (
            <div className="bg-zinc-900 border border-green-500 rounded-lg p-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-white mb-4">
                Pagamento completato!
              </h1>
              <p className="text-zinc-400 mb-6">
                Grazie per il tuo ordine. Riceverai una conferma via email.
              </p>
              {orderData && (
                <p className="text-green-500 font-semibold mb-6">
                  Numero ordine: {orderData.orderNumber}
                </p>
              )}
              <button
                onClick={() => navigate(orderData ? `/conferma-ordine/${orderData.orderId}` : "/")}
                className="w-full bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 transition-all"
              >
                Vedi dettagli ordine
              </button>
            </div>
          )}

          {status === "failed" && (
            <div className="bg-zinc-900 border border-red-500 rounded-lg p-8">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-white mb-4">
                Pagamento non riuscito
              </h1>
              <p className="text-zinc-400 mb-6">
                Si è verificato un problema con il pagamento. Riprova.
              </p>
              <button
                onClick={() => navigate("/checkout")}
                className="w-full bg-yellow-500 text-black py-3 rounded-lg font-bold hover:bg-yellow-600 transition-all"
              >
                Torna al checkout
              </button>
            </div>
          )}

          {status === "expired" && (
            <div className="bg-zinc-900 border border-yellow-500 rounded-lg p-8">
              <XCircle className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-white mb-4">
                Sessione scaduta
              </h1>
              <p className="text-zinc-400 mb-6">
                La sessione di pagamento è scaduta. Riprova.
              </p>
              <button
                onClick={() => navigate("/checkout")}
                className="w-full bg-yellow-500 text-black py-3 rounded-lg font-bold hover:bg-yellow-600 transition-all"
              >
                Torna al checkout
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentCompletePage;
